import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBooking } from 'contexts/BookingContext';
import { useAuth } from 'contexts/AuthContext';
import { getAvailability } from 'services/mewsService';
import { format } from 'date-fns';
import Toast from 'components/ux/toast/Toast';

const Availability = () => {
  const { state, dispatch } = useBooking();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const { checkIn, checkOut, config, roomLines } = state;

  useEffect(() => {
    if (!checkIn || !checkOut) {
      navigate('/');
    }
  }, [checkIn, checkOut, navigate]);

  const fetchAvailability = useCallback(async () => {
    if (!checkIn || !checkOut) return;
    try {
      setLoading(true);
      setError('');
      const startUtc = new Date(checkIn).toISOString();
      const endUtc = new Date(checkOut).toISOString();
      const result = await getAvailability(startUtc, endUtc);
      dispatch({ type: 'SET_AVAILABILITY', availability: result.availability, pricing: result.pricing });
    } catch (err) {
      if (err.status === 401) {
        logout();
        navigate('/login');
        return;
      }
      setError(`Failed to load availability: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [checkIn, checkOut, dispatch, logout, navigate]);

  useEffect(() => {
    fetchAvailability();
  }, [fetchAvailability]);

  if (!checkIn || !checkOut) return null;

  // Config failed to load on the search page — show a way back instead of a blank page
  if (!config) {
    return (
      <div className="max-w-lg mx-auto px-4 py-16 text-center">
        <div className="text-4xl text-red-400 mb-4">!</div>
        <h1 className="text-xl font-medium text-brand-dark mb-2">Could not load room configuration</h1>
        <p className="text-brand-muted mb-6">
          The room categories could not be loaded. Please go back and try again.
        </p>
        <button type="button" onClick={() => navigate('/')} className="btn-gold">
          Back to search
        </button>
      </div>
    );
  }

  const nights = Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24));

  const categoryAvailabilities = state.availability?.CategoryAvailabilities || [];
  const categories = config.categories || [];

  const categoryAvailMap = {};
  categoryAvailabilities.forEach((ca) => {
    // Mews returns one entry per time-unit start, including the checkout boundary.
    // Only consider the first `nights` entries (the actual stay nights).
    const availabilities = (ca.Availabilities || []).slice(0, nights);
    const minAvail = availabilities.length > 0 ? Math.min(...availabilities) : 0;
    const catId = ca.CategoryId || ca.ResourceCategoryId;
    categoryAvailMap[catId] = minAvail;
  });

  const categoryPrices = {};
  const pricingCurrency = state.pricing?.Currency || config.enterprise?.currency || 'DKK';

  // Mews returns one price per time-unit start, including the checkout boundary.
  // We only want the first `nights` entries (one per actual night stayed).
  const sumPrices = (prices) => {
    const slice = prices.slice(0, nights);
    return slice.reduce((sum, p) => {
      if (typeof p === 'number') return sum + p;
      return sum + (p.TotalAmount?.Value || p.GrossValue || p.Value || 0);
    }, 0);
  };

  if (state.pricing?.CategoryPrices) {
    state.pricing.CategoryPrices.forEach((cp) => {
      const prices = cp.Prices || [];
      if (prices.length > 0) {
        const total = sumPrices(prices);
        const catId = cp.CategoryId || cp.ResourceCategoryId;
        categoryPrices[catId] = {
          totalPrice: total,
          perNight: total / nights,
          currency: pricingCurrency,
        };
      }
    });
  }

  if (state.pricing?.BasePrices) {
    const totalBase = sumPrices(state.pricing.BasePrices);
    categories.forEach((cat) => {
      if (!categoryPrices[cat.id]) {
        categoryPrices[cat.id] = {
          totalPrice: totalBase,
          perNight: totalBase / nights,
          currency: pricingCurrency,
        };
      }
    });
  }

  const selectedCountByCategory = {};
  roomLines.forEach((line) => {
    selectedCountByCategory[line.categoryId] =
      (selectedCountByCategory[line.categoryId] || 0) + line.quantity;
  });

  const addRoomLine = (category) => {
    const price = categoryPrices[category.id];
    dispatch({
      type: 'ADD_ROOM_LINE',
      roomLine: {
        categoryId: category.id,
        categoryName: category.name,
        quantity: 1,
        rateId: config.nordicRate?.Id || '',
        pricePerNight: price?.perNight || 0,
        totalPrice: price?.totalPrice || 0,
        currency: price?.currency || 'DKK',
        flag: 'REQUEST',
      },
    });
  };

  const totalRooms = roomLines.reduce((sum, line) => sum + line.quantity, 0);
  const totalPrice = roomLines.reduce((sum, line) => sum + line.totalPrice * line.quantity, 0);

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/')}
          className="text-brand-gold hover:text-brand-gold-dark text-sm mb-3 inline-flex items-center gap-1 transition-colors"
        >
          <span>&larr;</span> Change dates
        </button>
        <h1 className="text-2xl font-medium text-brand-dark">Room Availability</h1>
        <p className="text-brand-muted mt-1">
          {format(checkIn, 'dd MMM yyyy')} &rarr; {format(checkOut, 'dd MMM yyyy')}
          <span className="font-mono text-xs ml-2 text-brand-muted/70">{nights} night{nights !== 1 ? 's' : ''}</span>
        </p>
        {config.nordicRate && (
          <span className="inline-block mt-2 text-xs text-brand-gold bg-brand-gold/10 px-3 py-1 rounded-pill">
            {config.nordicRate.Name || 'Nordic Agent Rate'}
          </span>
        )}
      </div>

      {error && (
        <div className="mb-4">
          <Toast message={error} type="error" dismissError={() => setError('')} />
        </div>
      )}

      {loading ? (
        <div className="text-center py-16">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-2 border-brand-gold border-t-transparent"></div>
          <p className="mt-3 text-brand-muted text-sm">Checking availability...</p>
        </div>
      ) : (
        <>
          {/* Room Categories */}
          <div className="space-y-4 mb-8">
            {categories.map((category) => {
              const available = categoryAvailMap[category.id] ?? 0;
              const price = categoryPrices[category.id];
              const selected = selectedCountByCategory[category.id] || 0;
              const remaining = available - selected;

              return (
                <div
                  key={category.id}
                  className="card p-5 flex flex-col md:flex-row md:items-center justify-between gap-4 hover:shadow-card-hover transition-shadow duration-200"
                >
                  <div className="flex-1">
                    <h3 className="font-medium text-lg text-brand-dark">{category.name}</h3>
                    {category.description && (
                      <p className="text-sm text-brand-muted mt-0.5">{category.description}</p>
                    )}
                    <p className="text-xs text-brand-muted/60 mt-1 font-mono">
                      Capacity: {category.capacity || '—'}
                      {category.extraCapacity > 0 && ` (+${category.extraCapacity})`}
                    </p>
                  </div>

                  <div className="flex items-center gap-5">
                    {price ? (
                      <div className="text-right">
                        <p className="text-xl font-medium text-brand-dark font-mono">
                          {Math.round(price.perNight).toLocaleString('da-DK')}
                          <span className="text-sm text-brand-muted ml-1">{price.currency}</span>
                        </p>
                        <p className="text-xs text-brand-muted">per night</p>
                        {nights > 1 && (
                          <p className="text-xs text-brand-muted/70 font-mono">
                            {Math.round(price.totalPrice).toLocaleString('da-DK')} total
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-brand-muted">Price on request</p>
                    )}

                    <div className="flex flex-col items-center gap-1.5">
                      <span className={`text-xs font-mono ${remaining > 0 ? 'text-brand-teal' : 'text-red-500'}`}>
                        {remaining > 0 ? `${remaining} avail` : 'Full'}
                      </span>
                      <button
                        onClick={() => addRoomLine(category)}
                        disabled={remaining <= 0}
                        className={`px-4 py-1.5 rounded-pill text-sm font-medium transition-colors ${
                          remaining > 0
                            ? 'bg-brand-teal text-white hover:bg-brand-teal-hover'
                            : 'bg-gray-100 text-gray-300 cursor-not-allowed'
                        }`}
                      >
                        + Add
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}

            {categories.length === 0 && !loading && (
              <p className="text-brand-muted text-center py-12">
                No room categories found. Check your Mews configuration.
              </p>
            )}
          </div>

          {/* Selected Room Lines */}
          {roomLines.length > 0 && (
            <div className="card p-6 mb-6">
              <h2 className="text-lg font-medium text-brand-dark mb-4">Selected Rooms</h2>

              <div className="space-y-3">
                {roomLines.map((line, index) => (
                  <div key={index} className="flex items-center justify-between border-b border-brand-border/50 pb-3">
                    <div className="flex items-center gap-3 flex-1">
                      <span className="font-medium text-brand-dark">{line.categoryName}</span>
                      <span className="text-brand-muted">&times;</span>
                      <input
                        type="number"
                        min="1"
                        max="10"
                        value={line.quantity}
                        onChange={(e) =>
                          dispatch({
                            type: 'UPDATE_ROOM_LINE',
                            index,
                            updates: { quantity: Math.max(1, parseInt(e.target.value) || 1) },
                          })
                        }
                        className="w-14 border border-brand-border rounded-md px-2 py-1 text-center text-sm font-mono focus:outline-none focus:border-brand-gold"
                      />
                    </div>

                    <div className="flex items-center gap-3">
                      <select
                        value={line.flag}
                        onChange={(e) =>
                          dispatch({
                            type: 'UPDATE_ROOM_LINE',
                            index,
                            updates: { flag: e.target.value },
                          })
                        }
                        className="border border-brand-border rounded-md px-2 py-1 text-sm focus:outline-none focus:border-brand-gold"
                      >
                        <option value="REQUEST">Request</option>
                        <option value="ALLOTMENT">Allotment</option>
                      </select>

                      <div className="text-right w-28">
                        <span className="block text-sm font-mono text-brand-dark">
                          {Math.round(line.totalPrice).toLocaleString('da-DK')} {line.currency}
                        </span>
                        <span className="text-xs text-brand-muted">total</span>
                      </div>

                      <button
                        onClick={() => dispatch({ type: 'REMOVE_ROOM_LINE', index })}
                        className="text-red-400 hover:text-red-600 text-lg px-1 transition-colors"
                        title="Remove"
                      >
                        &times;
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4 pt-4 border-t border-brand-border/50 flex justify-between items-center">
                <span className="text-brand-muted text-sm">
                  {totalRooms} room{totalRooms !== 1 ? 's' : ''}
                </span>
                <span className="text-xl font-medium text-brand-dark font-mono">
                  {Math.round(totalPrice).toLocaleString('da-DK')} {roomLines[0]?.currency || 'DKK'}
                </span>
              </div>
            </div>
          )}

          {/* Continue button */}
          {roomLines.length > 0 && (
            <div className="text-right">
              <button
                onClick={() => navigate('/booking')}
                className="btn-gold"
              >
                Continue to Guest Details
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Availability;
